import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// ScrollSmoother requires ScrollTrigger
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin, SplitText);
import Lenis from "@studio-freight/lenis";

/* 

Loading Animation

*/
const loadAnimation = document.querySelector(".loading-animation");
const loadingOverlay = document.querySelector(".loading-overlay");
const main = document.querySelector("main");
const header = document.querySelector(".section-header");

document.addEventListener("DOMContentLoaded", async () => {
  // フォントの読み込みを待つ
  await window.waitForFonts();

  // ページの読み込みが完了したときに実行される関数
  const isFirstLoad = sessionStorage.getItem("isFirstLoad");
  const fvVideo = document.querySelector(".js-fv-video video");
  // フラグがない場合（初回アクセス時）
  if (!isFirstLoad) {
    // 初回アクセス時の処理を記述
    if (fvVideo) {
      fvVideo.pause();
    }
    if (loadingOverlay) {
      gsap.to(loadingOverlay, {
        opacity: 0,
        duration: 0.3,
        delay: 0.6,
        onComplete: () => {
          gsap.set(loadingOverlay, { display: "none" });
        },
      });
    }
    if (loadAnimation) {
      gsap.to(loadAnimation, {
        yPercent: -101,
        duration: 0.3,
        delay: 0.3,
        ease: "power2.in",
        onComplete: () => {
          gsap.set(loadAnimation, { display: "none" });
          if (fvVideo) {
            fvVideo.play();
          }
        },
      });
    }
    if (main) {
      gsap.to(main, {
        opacity: 1,
        visibility: "visible",
        delay: 0.05,
      });
    }
    if (header) {
      gsap.to(header, {
        opacity: 1,
        visibility: "visible",
        delay: 0.05,
      });
    }
    // セッションストレージにフラグを保存
    sessionStorage.setItem("isFirstLoad", "true");
  } else {
    if (loadingOverlay) {
      // 2回目以降のアクセス時の処理を記述
      gsap.to(loadingOverlay, {
        opacity: 0,
        display: "none",
        duration: 0,
      });
    }
    if (loadAnimation) {
      gsap.to(loadAnimation, {
        opacity: 0,
        display: "none",
        duration: 0.3,
        delay: 0.3,
      });
    }
    if (main) {
      gsap.to(main, {
        opacity: 1,
        visibility: "visible",
        duration: 0.3,
        delay: 0.3,
      });
    }
    if (header) {
      gsap.to(header, {
        opacity: 1,
        visibility: "visible",
        duration: 0.3,
        delay: 0.3,
      });
    }
  }
});

/* 

Lenis

*/
const lenis = new Lenis({
  duration: 0.5,
  easing: (t) => Math.min(1, 1.0001 - Math.pow(2, -10 * t)),
  direction: "vertical",
  gestureDirection: "vertical",
  smooth: true,
  infinite: false,
  autoResize: true,
});

lenis.on("scroll", ScrollTrigger.update);

function raf(time) {
  lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

gsap.ticker.add((time) => {
  lenis.raf(time * 1000);
});

gsap.ticker.lagSmoothing(0);

// smooth scroll
const smoothScrollTriggers = document.querySelectorAll("[href^='#']");
if (smoothScrollTriggers.length > 0) {
  smoothScrollTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      const target = document.querySelector(trigger.getAttribute("href"));
      if (target) {
        lenis.scrollTo(target, {
          offset: -80,
          duration: 1,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -8 * t)),
        });
      }
    });
  });
}

/* 


変数


*/

/* 

FVのスクロールアニメーション

*/
const videoInner = document.querySelector(".js-fv-video-inner");
let headerHeight = header.offsetHeight;
if (videoInner) {
  // headerのマージン
  const headerMargin = () => {
    headerHeight = header.offsetHeight;
    header.style.marginBottom = -1 * headerHeight + "px";
  };
  headerMargin();
  window.addEventListener("resize", headerMargin);

  // FVのvideoがある状態でロードしたときに、is-topを付与（htmlにも記載している）
  if (!document.querySelector(".section-header").classList.contains("is-top")) {
    document.querySelector(".section-header").classList.add("is-top");
  }

  // headerのis-topの付与
  if (header) {
    gsap.to(header, {
      scrollTrigger: {
        toggleActions: "play none none reverse", // スクロールで外れる
        trigger: videoInner, // アニメーションが始まるトリガーとなる要素
        start: "top bottom", // アニメーションが始まる位置
        toggleClass: {
          targets: header, // クラスを切り替える対象の要素
          className: "is-top",
        },
      },
    });
  }

  // FVの動画のスクロールアニメーション
  const videoScrollFunction = () => {
    // 新しいタイムラインとScrollTriggerを作成
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: videoInner,
        start: "top top-=100px",
        end: "80% bottom",
        invalidateOnRefresh: true,
        scrub: true,
        onUpdate: () => {
          headerHeight = header.offsetHeight; // headerHeightを更新
        },
      },
    });
    const tl2 = gsap.timeline({
      scrollTrigger: {
        trigger: videoInner,
        start: "bottom 90%",
        end: "bottom top",
        invalidateOnRefresh: true,
        scrub: true,
      },
    });
    const scrollAnimation = () => {
      tl.to(".js-fv-video-content", {
        width: `calc(100% - 40px)`,
        height: () => `calc(33.333333% - ${header.offsetHeight}px - 20px)`, // 動的に取得
        marginBottom: "20px",
        top: () => header.offsetHeight, // 動的に取得
        borderRadius: "10px",
        duration: 1,
        ease: "power1.out",
      }).to(
        ".js-header-brightness",
        {
          filter: "invert(0)",
        },
        "<"
      );
      tl2
        .to(".js-fv-video-content", {
          y: `10vw`,
        })
        .to(
          ".matenro-video__bg",
          {
            y: `10vw`,
          },
          "<"
        );
    };
    scrollAnimation();
  };
  // 初期アニメーションを作成
  videoScrollFunction();
} else {
  // FVの動画がないとき
  // headerのis-topの付与
  if (header) {
    gsap.to(header, {
      scrollTrigger: {
        toggleActions: "play none none reverse", // スクロールで外れる
        trigger: "header", // アニメーションが始まるトリガーとなる要素
        start: "top bottom", // アニメーションが始まる位置
        toggleClass: {
          targets: header, // クラスを切り替える対象の要素
          className: "is-top",
        },
      },
    });
  }
}

const splideImgs = document.querySelectorAll(".matenro-image-slider__image[data-src]");
if (splideImgs) {
  splideImgs.forEach((img) => {
    const setStyle = `
  transform: rotate(${(Math.random() - Math.random()) * 15}deg)
              translateY(${(Math.random() - Math.random()) * 15}%);
  `;
    img.setAttribute("style", setStyle);
  });
}

/* 

card

*/
const cards = document.querySelectorAll(".js-card");
if (cards) {
  // カードのイベントリスナーを保持する配列
  let cardListeners = [];

  // イベントリスナーをクリーンアップする関数
  function cleanupListeners() {
    cardListeners.forEach(({ card, enter, leave }) => {
      card.removeEventListener("mouseenter", enter);
      card.removeEventListener("mouseleave", leave);
    });
    cardListeners = [];
  }

  // カードアニメーションの設定を行う関数
  function setupCardAnimation() {
    // 既存のリスナーをクリーンアップ
    cleanupListeners();
    let mm = gsap.matchMedia();
    // デスクトップ用アニメーション
    mm.add("(min-width: 480px)", () => {
      cards.forEach((card) => {
        const enterHandler = () => {
          gsap.to(card, {
            scale: 1.02,
            opacity: 0.85,
            duration: 0.3,
            ease: "power2.out",
          });
        };
        const leaveHandler = () => {
          gsap.to(card, {
            scale: 1,
            opacity: 1,
            duration: 0.3,
            ease: "power2.out",
          });
        };
        card.addEventListener("mouseenter", enterHandler);
        card.addEventListener("mouseleave", leaveHandler);
        cardListeners.push({ card, enter: enterHandler, leave: leaveHandler });
      });
    });
    // モバイル用アニメーション
    mm.add("(max-width: 479px)", () => {
      cards.forEach((card) => {
        const enterHandler = () => {
          gsap.to(card, {
            scale: 1.02,
            opacity: 0.85,
            duration: 0.3,
            ease: "power2.out",
          });
        };
        const leaveHandler = () => {
          gsap.to(card, {
            scale: 1,
            opacity: 1,
            duration: 0.3,
            ease: "power2.out",
          });
        };
        card.addEventListener("mouseenter", enterHandler);
        card.addEventListener("mouseleave", leaveHandler);
        cardListeners.push({ card, enter: enterHandler, leave: leaveHandler });
      });
    });
    return mm;
  }

  // 初期設定
  let mm = setupCardAnimation();

  // リサイズ時の処理
  let resizeTimeout;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      if (mm) {
        mm.revert();
      }
      mm = setupCardAnimation();
    }, 250);
  });
}
/* 

faq

*/

const faqItem = document.querySelectorAll(".matenro-faq__item");
if (faqItem) {
  faqItem.forEach((item) => {
    item.addEventListener("click", () => {
      item.classList.toggle("is-open");
    });
  });
}
/* 

policy

*/
const policyLink = document.querySelector("a[href='#policy']");
const policyItem = document.querySelector(".product__content .product__policy");
const policyTitle = document.querySelector(".product__content .product__policy .product__content-item-subtitle");
if (policyTitle) {
  policyTitle.addEventListener("click", () => {
    policyItem.classList.toggle("is-open");
  });
}
if (policyLink) {
  policyLink.addEventListener("click", () => {
    const isOpen = policyItem.classList.contains("is-open");
    if (!isOpen) {
      clearTimeout();
      setTimeout(() => {
        policyItem.classList.add("is-open");
      }, 500);
    }
  });
}

/* 

footer

*/
document.addEventListener("DOMContentLoaded", async () => {
  // フォントの読み込みを待つ
  await window.waitForFonts();

  const footerContact = document.querySelector(".footer__contact-link");
  if (footerContact) {
    const footerContactHeading = new SplitText(footerContact.querySelector(".footer__contact-heading"), { type: "chars" });
    const footerContactHoverAnimation = gsap
      .timeline({ paused: true })
      .to(footerContactHeading.chars, {
        xPercent: 100,
        opacity: 0,
        stagger: 0.01,
        duration: 0.15,
        ease: "power1.in",
      })
      .to(footerContactHeading.chars, {
        xPercent: -50,
        duration: 0,
      })
      .to(footerContactHeading.chars, {
        xPercent: 0,
        opacity: 1,
        duration: 0.2,
        stagger: 0.01,
        ease: "power1.out",
      });
    footerContact.addEventListener("mouseenter", () => {
      footerContactHoverAnimation.restart();
    });
  }
});
/* 

button

*/
document.addEventListener("DOMContentLoaded", async () => {
  // フォントの読み込みを待つ
  await window.waitForFonts();
  const buttons = document.querySelectorAll(".matenro-button__inner");
  if (buttons) {
    buttons.forEach((btn) => {
      const split = new SplitText(btn.querySelectorAll("span"), { type: "chars" });
      const img = btn.querySelector("img");
      const tl = gsap
        .timeline({ paused: true })
        .to(split.chars, {
          yPercent: -100,
          opacity: 1,
          duration: 0.1,
          stagger: 0.02,
          ease: "power2.out",
        })
        .to(
          img,
          {
            xPercent: 25,
            duration: 0.3,
            ease: "power2.inOut",
          },
          "<"
        );
      btn.addEventListener("mouseenter", () => {
        tl.restart();
      });
      btn.addEventListener("mouseleave", () => {
        tl.reverse();
      });
    });
  }
});
/* 

contact other methods

*/
const contactOtherMethods = document.querySelector(".contact__other-methods");
if (contactOtherMethods) {
  contactOtherMethods.addEventListener("click", function () {
    this.classList.add("is-open");
  });
}

const predictiveSearchHeader = document.getElementById("menu-drawer");
if (predictiveSearchHeader) {
  predictiveSearchHeader.addEventListener(
    "wheel",
    (event) => {
      event.stopPropagation();
    },
    { passive: true }
  );

  predictiveSearchHeader.addEventListener(
    "touchmove",
    (event) => {
      event.stopPropagation();
    },
    { passive: true }
  );
}
const cartInner = document.querySelector(".cart-drawer .drawer__inner");
if (cartInner) {
  cartInner.addEventListener(
    "wheel",
    (event) => {
      event.stopPropagation();
    },
    { passive: true }
  );

  cartInner.addEventListener(
    "touchmove",
    (event) => {
      event.stopPropagation();
    },
    { passive: true }
  );
}

/* Google Reviews */
const GoogleReviews = document.querySelector(".tagembed-container");
setTimeout(() => {
  if (GoogleReviews) {
    const credit = document.querySelector(".tb_hc_post_container").nextElementSibling;
    credit.remove();
  }
}, 2000);

/* 

fadeIn

*/

window.addEventListener("load", async () => {
  // フォントの読み込みを待つ
  await window.waitForFonts();
  const fadeInElements = document.querySelectorAll(".js-fadeIn");

  // 固定ボタン用のタイムラインは一度だけ作成
  let fixedButtonTl = null;
  const productInfoContainer = document.querySelector(".product__info-container");

  if (fadeInElements.length > 0) {
    fadeInElements.forEach((fadeInElement) => {
      // 必要なタイムラインだけを遅延初期化
      let timelines = {};

      const createTimeline = (key, config) => {
        if (!timelines[key]) {
          timelines[key] = gsap.timeline(config);
        }
        return timelines[key];
      };

      // クラス名をキャッシュして効率化
      const classList = fadeInElement.classList;
      const classes = {
        maskContainer: classList.contains("mask-container"),
        driftIllust: classList.contains("drift-illust"),
        imageTextParagraph: classList.contains("image-text-vertical__content-text-paragraph"),
        matenroTitle: classList.contains("matenro-title"),
        matenroTitleSub: classList.contains("matenro-title-sub"),
        matenroTextL: classList.contains("matenro-text-l"),
        matenroTextM: classList.contains("matenro-text-m"),
        matenroButton: classList.contains("matenro-button"),
        matenroTitleLower: classList.contains("matenro-title-lower"),
        fadeInUp: classList.contains("fadeInUp"),
        imageSliderWrapper: classList.contains("matenro-image-slider__list-wrapper"),
        contactLink: classList.contains("js-contact-link"),
        bannerMediaImage: classList.contains("banner__media-image"),
        fixedButton: classList.contains("js-fixed-button"),
      };

      // mask-container の処理
      if (classes.maskContainer) {
        const tl6 = createTimeline("tl6", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=200",
            invalidateOnRefresh: true,
          },
        });
        const paths = fadeInElement.querySelectorAll("path");
        paths.forEach((path) => {
          tl6.add(() => {
            path.classList.add("js-animated");
          });
        });
      }

      // drift-illust の処理
      if (classes.driftIllust) {
        const tl6 = createTimeline("tl6", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=200",
            invalidateOnRefresh: true,
          },
        });
        tl6.to(fadeInElement, {
          transform: "rotateY(0deg) translateX(0px) scale(1)",
          opacity: 1,
          duration: 1,
          delay: 0.35,
          ease: "power4.inOut",
        });
        const driftEffect = fadeInElement.nextElementSibling;
        if (driftEffect) {
          tl6.to(
            driftEffect,
            {
              delay: 0.5,
              animationPlayState: "running",
            },
            "<"
          );
        }
      }

      // image-text-vertical__content-text-paragraph の処理
      if (classes.imageTextParagraph) {
        const tl7 = createTimeline("tl7", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=150",
            end: "bottom bottom-=300",
            invalidateOnRefresh: true,
            scrub: true,
          },
        });
        tl7.to(fadeInElement, {
          opacity: 1,
          duration: 1,
        });
      }

      // matenro-title の処理
      if (classes.matenroTitle) {
        const tl = createTimeline("tl", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=100",
            invalidateOnRefresh: true,
          },
        });
        const split = new SplitText(fadeInElement, {
          type: "chars, words",
          wordsClass: "matenro-title-word",
          charsClass: "matenro-title-char",
        });
        tl.to(split.chars, {
          y: 0,
          stagger: 0.03,
          duration: 0.5,
          ease: "power3.inOut",
        });
      }

      // text系の処理（統合）
      if (classes.matenroTitleSub || classes.matenroTextL || classes.matenroTextM) {
        const tl = createTimeline("tl", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=100",
            invalidateOnRefresh: true,
          },
        });
        const split = new SplitText(fadeInElement, { type: "words", wordsClass: "matenro-title-text-word" });
        tl.to(split.words, {
          y: 0,
          opacity: 1,
          delay: 0.2,
          duration: 0.8,
          ease: "power3.out",
        });
      }

      // button系の処理（統合）
      if (classes.matenroButton || classes.matenroTitleLower || classes.fadeInUp) {
        const tl = createTimeline("tl", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom-=100",
            invalidateOnRefresh: true,
          },
        });
        const duration = classes.fadeInUp ? 0.7 : 0.8;
        const ease = classes.fadeInUp ? "power2.inOut" : "power2.out";

        tl.fromTo(
          fadeInElement,
          {
            y: 50,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: duration,
            ease: ease,
          }
        );
      }

      // matenro-image-slider__list-wrapper の処理
      if (classes.imageSliderWrapper) {
        const tl5 = createTimeline("tl5", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom+=100",
            end: "bottom top-=100",
            invalidateOnRefresh: true,
            scrub: true,
          },
        });
        tl5.fromTo(
          fadeInElement,
          {
            yPercent: 45,
          },
          {
            yPercent: -20,
          }
        );
      }

      // js-contact-link の処理
      if (classes.contactLink) {
        const tl2 = createTimeline("tl2", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: "top bottom",
            invalidateOnRefresh: true,
          },
        });
        tl2.fromTo(
          fadeInElement,
          {
            scale: 0.65,
          },
          {
            scale: 1,
            duration: 1,
            ease: "power3.out",
          }
        );
      }

      // banner__media-image の処理
      if (classes.bannerMediaImage) {
        const tl3 = createTimeline("tl3", {
          scrollTrigger: {
            trigger: fadeInElement,
            start: () => `top ${fadeInElement.getBoundingClientRect().top}px`,
            end: "bottom top",
            invalidateOnRefresh: true,
            scrub: true,
          },
        });
        tl3.fromTo(
          fadeInElement,
          {
            objectPosition: "0% 40%",
          },
          {
            objectPosition: "0% 90%",
          }
        );
      }

      // js-fixed-button の処理（一度だけタイムライン作成）
      if (classes.fixedButton && productInfoContainer) {
        if (!fixedButtonTl) {
          fixedButtonTl = gsap.timeline({
            scrollTrigger: {
              trigger: ".product__info-container",
              start: "center top",
              toggleActions: "play none none reverse",
              invalidateOnRefresh: true,
            },
          });
        }
        fixedButtonTl.to(fadeInElement, {
          yPercent: -100,
          duration: 0.5,
          ease: "power2.out",
        });
      }
    });
  }
});
